import { useRef } from 'react';
import { gsap, useGSAP, EASE_DISPLAY } from './gsap';

/**
 * Resting scale. The media stays slightly over-scaled so the parallax drift
 * always has headroom and never exposes an edge inside the frame.
 */
const REST_SCALE = 1.12;
/** Scale the entry zoom starts from. */
const ENTRY_SCALE = 1.3;

type Options = {
  /** Selector for the media element inside the frame. */
  selector?: string;
  /** How far the media drifts across the full scroll pass, in percent. */
  drift?: number;
  /** Play the clip-path wipe on entry. */
  reveal?: boolean;
  /** Delay the reveal, for staggering a grid of frames. */
  delay?: number;
  /**
   * Play the reveal on mount instead of waiting for a ScrollTrigger. Use it for
   * above-the-fold media: a trigger there only fires once the image has loaded
   * and ScrollTrigger has refreshed, which reads as a late, laggy entrance.
   */
  immediate?: boolean;
};

/**
 * Frames a piece of media: a clip-path wipe on entry, a slow zoom out to the
 * resting scale, then a parallax drift tied to scroll position.
 *
 * Returns a ref for the *frame* — the element with `overflow: hidden`.
 *
 * Both tweens write to the same element's transform, so both use `fromTo` with
 * explicit endpoints. With a bare `from`, the zoom captures its end value after
 * the parallax has already written a transform and the media stays stuck at the
 * entry scale.
 */
export function useParallaxMedia<T extends HTMLElement>({
  selector = 'img',
  drift = 12,
  reveal = true,
  delay = 0,
  immediate = false,
}: Options = {}) {
  const frame = useRef<T>(null);

  useGSAP(
    () => {
      const el = frame.current;
      if (!el) return;

      const mm = gsap.matchMedia();

      mm.add('(prefers-reduced-motion: no-preference)', () => {
        gsap.set(selector, { scale: REST_SCALE });

        if (reveal) {
          const tl = gsap.timeline(
            immediate
              ? {}
              : { scrollTrigger: { trigger: el, start: 'top 88%', once: true } },
          );

          tl.fromTo(
            el,
            { clipPath: 'inset(100% 0% 0%)' },
            { clipPath: 'inset(0% 0% 0%)', duration: 1.1, ease: EASE_DISPLAY, delay },
          ).fromTo(
            selector,
            { scale: ENTRY_SCALE },
            { scale: REST_SCALE, duration: 1.4, ease: EASE_DISPLAY },
            '<',
          );
        }

        // Runs on its own ScrollTrigger so the scrubbed drift never shares a
        // timeline with the one-shot entry.
        gsap.fromTo(
          selector,
          { yPercent: -drift / 2 },
          {
            yPercent: drift / 2,
            ease: 'none',
            scrollTrigger: {
              trigger: el,
              start: 'top bottom',
              end: 'bottom top',
              scrub: true,
            },
          },
        );
      });

      return () => mm.revert();
    },
    { scope: frame, dependencies: [selector, drift, reveal, delay, immediate] },
  );

  return frame;
}
